/*
 * Назначение: вкладка «Модели» в настройках проекта.
 * Функционал: список моделей с приоритетом (основная + резервные), у каждой —
 * провайдер из «Интеграций» (или «любой доступный»), порядок стрелками,
 * вкл/выкл, тест одного запроса к связке модель+провайдер.
 * Готового списка моделей в коде нет сознательно: имена моделей — данные
 * провайдера, оператор вписывает их так, как называет его провайдер.
 */

import { useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import type { ModelConfigEntry, ProjectDetails } from "../../../lib/types";

import { ModelRow, type TestResult } from "./model-row";
import { ProviderSelect, useAiProviders } from "./provider-select";
import { useProjectMutation } from "./shared";

/** Ключ результата теста: одна модель может стоять у разных провайдеров. */
const testKey = (entry: { model: string; provider_id: string }) =>
  `${entry.model}@${entry.provider_id}`;

export function ModelsTab({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить настройки моделей");
  const providers = useAiProviders();
  const [models, setModels] = useState<ModelConfigEntry[]>([]);
  const [newModel, setNewModel] = useState("");
  const [newProvider, setNewProvider] = useState("");
  const [tests, setTests] = useState<Record<string, TestResult>>({});

  useEffect(() => {
    setModels(
      [...(project.models_config ?? [])]
        .sort((a, b) => a.order - b.order)
        .map((m, i) => ({
          model: m.model,
          provider_id: m.provider_id ?? "",
          active: m.active !== false,
          order: i,
          max_tokens: m.max_tokens ?? 0,
        })),
    );
  }, [project]);

  const move = (index: number, dir: -1 | 1) => {
    setModels((current) => {
      const next = [...current];
      const target = index + dir;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((m, i) => ({ ...m, order: i }));
    });
  };

  const remove = (index: number) =>
    setModels((current) =>
      current.filter((_, i) => i !== index).map((m, i) => ({ ...m, order: i })),
    );

  const toggle = (index: number, active: boolean) =>
    setModels((current) => current.map((m, i) => (i === index ? { ...m, active } : m)));

  const setProvider = (index: number, provider_id: string) =>
    setModels((current) => current.map((m, i) => (i === index ? { ...m, provider_id } : m)));

  const setMaxTokens = (index: number, max_tokens: number) =>
    setModels((current) => current.map((m, i) => (i === index ? { ...m, max_tokens } : m)));

  const addModel = () => {
    const model = newModel.trim();
    if (!model) return;
    if (models.some((m) => m.model === model && m.provider_id === newProvider)) {
      toast.error("Такая связка модель + провайдер уже в списке");
      return;
    }
    setModels((current) => [
      ...current,
      { model, provider_id: newProvider, active: true, order: current.length, max_tokens: 0 },
    ]);
    setNewModel("");
  };

  const runTest = async (entry: ModelConfigEntry) => {
    const key = testKey(entry);
    setTests((t) => ({ ...t, [key]: { loading: true } }));
    try {
      const res = await projectsApi.testModel(
        entry.model,
        undefined,
        entry.provider_id,
      );
      setTests((t) => ({
        ...t,
        [key]: {
          loading: false,
          ok: res.success,
          reply: res.reply ?? undefined,
          error: res.error ?? undefined,
          latency: res.latency_ms,
        },
      }));
    } catch (err) {
      setTests((t) => ({
        ...t,
        [key]: {
          loading: false,
          ok: false,
          error: err instanceof ApiError ? err.detail : "Не удалось протестировать",
        },
      }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Модели проекта</CardTitle>
        <CardDescription>
          Сначала идёт основная модель, затем резервные. У каждой модели — свой
          провайдер из раздела «Интеграции»; «любой доступный» значит, что модель
          будет спрошена у всех провайдеров по порядку. При сбое или таймауте
          запрос уходит к следующей связке. «Лимит ответа» у модели перекрывает
          лимит из параметров генерации — моделям с рассуждениями (gpt-oss и др.)
          нужен большой лимит. Минимум одна активная модель обязательна.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {models.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            Список пуст — ИИ отвечать не будет. Добавь модель и выбери, у какого
            провайдера её спрашивать.
          </div>
        ) : (
          models.map((entry, index) => (
            <ModelRow
              key={testKey(entry)}
              entry={entry}
              index={index}
              total={models.length}
              providers={providers}
              test={tests[testKey(entry)]}
              onMove={move}
              onToggle={toggle}
              onProvider={setProvider}
              onMaxTokens={setMaxTokens}
              onTest={runTest}
              onRemove={remove}
            />
          ))
        )}

        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            placeholder="Имя модели так, как её называет провайдер"
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
            className="md:flex-1"
          />
          <ProviderSelect
            value={newProvider}
            providers={providers}
            onChange={setNewProvider}
            className="md:w-[220px]"
          />
          <Button variant="outline" onClick={addModel}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить
          </Button>
        </div>

        <div>
          <Button
            onClick={() => mutation.mutate({ models_config: models })}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить порядок и состав
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
